/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { PickupInfo } from './get-show-contact-info';

export interface IAddContactInfoInput {
    inputRef?: React.RefObject<HTMLInputElement>;
    pickupNameInputRef?: React.RefObject<HTMLInputElement>;
    hasError: boolean;
    email: string;
    newsletter: boolean;
    pickupInfo: PickupInfo | null;
    pickupNameInvalid: boolean;
    resources: {
        emailLabel: string;
        emailErrortext: string;
        saveBtnLabel: string;
        cancelBtnLabel: string;
        newsletterLabel: string;
        pickupNameLabel: string;
        pickupNotesLabel: string;
        pickupNameErrorText: string;
    };
    canSubmit: boolean;
    canCancel: boolean;
    onChange(email: string, newsletter: boolean, pickupInfo: PickupInfo | null): void;
    onSubmit(): void;
    onCancel(): void;
}

export interface IAddContactInfo {
    addFormProps: INodeProps;
    submitButton: React.ReactNode;
    cancelButton: React.ReactNode;
    inputGroupProps: INodeProps;
    label: React.ReactNode;
    error: React.ReactNode;
    input: React.ReactNode;
    newsletter: React.ReactNode;
    pickupName?: React.ReactNode;
    pickupNotes?: React.ReactNode;
}

const getAddContactInfo = ({
    hasError,
    inputRef,
    pickupNameInputRef,
    email,
    canSubmit,
    canCancel,
    newsletter,
    pickupInfo,
    pickupNameInvalid,
    onChange,
    onSubmit,
    onCancel,
    resources: {
        emailLabel,
        emailErrortext,
        saveBtnLabel,
        cancelBtnLabel,
        newsletterLabel,
        pickupNameLabel,
        pickupNotesLabel,
        pickupNameErrorText
    }
}: IAddContactInfoInput): IAddContactInfo => {
    return {
        addFormProps: { className: 'ms-checkout-guest-profile__add-contact-info' },
        submitButton: canSubmit && (
            <Button className='ms-checkout-guest-profile__btn-save' title={saveBtnLabel} color='primary' onClick={onSubmit}>
                {saveBtnLabel}
            </Button>
        ),
        cancelButton: canCancel && (
            <Button className='ms-checkout-guest-profile__btn-cancel' title={cancelBtnLabel} color='secondary' onClick={onCancel}>
                {cancelBtnLabel}
            </Button>
        ),
        inputGroupProps: { className: classnames('ms-checkout-guest-profile__input', { 'is-invalid': hasError }) },
        label: (
            <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                {emailLabel}
            </label>
        ),
        error: hasError && (
            <span id='ms-checkout-guest-profile__error' className='ms-checkout-guest-profile__input-error' role='alert' aria-live='polite'>
                {emailErrortext}
            </span>
        ),
        newsletter: (
            <div>
                <span className='hei-checkout-guest-profile__newsletter'>
                    <label>
                        <input
                            type='checkbox'
                            checked={newsletter}
                            onChange={evt => onChange(email || '', evt.target.checked, pickupInfo)}
                        />{' '}
                        {newsletterLabel}
                    </label>
                </span>
            </div>
        ),
        input: (
            <input
                ref={inputRef}
                type='text'
                className='ms-checkout-guest-profile__input-text form-control'
                aria-label={emailLabel}
                onChange={evt => onChange(evt.target.value || '', newsletter, pickupInfo)}
                value={email}
                aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
            />
        ),
        pickupName: pickupInfo && (
            <>
                <label className='ms-checkout-guest-profile__input-label hei-checkout-guest-profile-section'>{pickupNameLabel}</label>
                {pickupNameInvalid && (
                    <span
                        id='ms-checkout-guest-profile__error'
                        className='ms-checkout-guest-profile__input-error'
                        role='alert'
                        aria-live='polite'
                    >
                        {pickupNameErrorText}
                    </span>
                )}
                <input
                    ref={pickupNameInputRef}
                    type='text'
                    className='ms-checkout-guest-profile__input-text form-control'
                    aria-label={pickupNameLabel}
                    onChange={evt =>
                        onChange(email, newsletter, {
                            ...pickupInfo,
                            name: evt.target.value || ''
                        })
                    }
                    value={pickupInfo.name ?? ''}
                    aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
                    maxLength={50}
                />
            </>
        ),
        pickupNotes: pickupInfo && (
            <>
                <label className='ms-checkout-guest-profile__input-label hei-checkout-guest-profile-section'>{pickupNotesLabel}</label>
                <textarea
                    className='ms-checkout-guest-profile__input-text input-textarea form-control'
                    aria-label={pickupNotesLabel}
                    onChange={evt =>
                        onChange(email, newsletter, {
                            ...pickupInfo,
                            notes: evt.target.value || ''
                        })
                    }
                    value={pickupInfo.notes ?? ''}
                    aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
                    maxLength={500}
                ></textarea>
            </>
        )
    };
};

export default getAddContactInfo;
